import { IDataItem } from "@dhx/ts-data";
import { ShapesCollection } from "../../ShapesCollection";
import { IDiagramConfig, ILineConfig } from "../../types";
import { directLinkPath } from "../linkPaths";
import { setHeaderColor } from "../placement";

function setLocalSizes(
	data: ShapesCollection,
	shape: IDataItem,
	config: IDiagramConfig,
	vbranch: number,
	extra: number
) {
	const kids = shape.$connection;
	const assistants = shape.$assistants;
	const partners = shape.$partners?.common;

	if (
		(shape.dir === "vertical" && partners?.length) ||
		(shape.$level === 0 && (shape.openDir || Array.isArray(shape.dir))) ||
		shape.dir === "verticalLeft" ||
		shape.dir === "verticalRight"
	) {
		delete shape.dir;
		delete shape.openDir;
	}

	const vertical = shape.dir === "vertical";
	const correction = vertical ? config.margin.itemX / 2 : 0;

	let kidsWidth = 0;
	let kidsHeight = 0;
	let baseHeight = 0;

	if (kids && shape.open !== false && !shape.assistant && !shape.partner) {
		let count = 0;
		for (let i = 0; i < kids.length; i++) {
			const sub = data.getItem(kids[i][1]);
			if (!sub.hidden && !sub.assistant && !sub.partner) {
				const dx = setLocalSizes(data, sub, config, vbranch + correction, correction);
				if (vertical) {
					kidsWidth = Math.max(kidsWidth, dx);
				} else {
					kidsWidth += dx;
				}
				count++;
			}
		}
		if (count && !vertical) {
			kidsWidth += (count - 1) * config.margin.itemX;
		}
		shape.$count = count;
	}

	if (partners?.length) {
		let leftSideHeight = 0;
		let rightSideHeight = 0;
		let maxSideWidth = 0;

		for (let index = 0; index < partners.length; index++) {
			const sub = data.getItem(partners[index]);
			const height = sub.height + config.margin.itemY;
			const width = sub.width + config.margin.itemX;

			sub.$even = !!(index % 2);

			if (!sub.hidden) {
				if (sub.$even) {
					rightSideHeight += height;
				} else {
					leftSideHeight += height;
				}
				maxSideWidth = Math.max(maxSideWidth, width);

				++shape.$count;
			}
		}
		baseHeight = Math.max(leftSideHeight, rightSideHeight);
		kidsWidth = Math.max(
			kidsWidth,
			!rightSideHeight
				? Math.max(maxSideWidth - config.margin.itemX / 2, shape.width + config.margin.itemX / 2) * 2
				: maxSideWidth * 2 + shape.width
		);

		shape.$partners.leftHeight = leftSideHeight;
		shape.$partners.rightHeight = rightSideHeight;
	}

	if (assistants?.length) {
		let maxSideWidth = 0;
		let leftSideHeight = 0;
		let rightSideHeight = 0;

		for (let index = 0; index < assistants.length; index++) {
			const sub = data.getItem(assistants[index]);
			const height = sub.height + config.margin.itemY;
			const width = sub.width + config.margin.itemX / 2;

			sub.$even = !!(index % 2);

			if (!sub.hidden) {
				if (sub.$even) {
					rightSideHeight += height;
				} else {
					leftSideHeight += height;
				}
				maxSideWidth = Math.max(maxSideWidth, width);

				++shape.$count;
			}
		}

		if (vertical) {
			kidsWidth = Math.max(kidsWidth, maxSideWidth);
			kidsHeight = leftSideHeight + rightSideHeight;
		} else {
			kidsWidth = Math.max(kidsWidth, maxSideWidth * 2);
			kidsHeight = Math.max(rightSideHeight, leftSideHeight);
		}
	}

	kidsWidth = Math.max(shape.width, kidsWidth);

	if (!vertical) {
		const gridStep = config.gridStep || 1;
		const width = (kidsWidth - shape.width) / 2 + vbranch;
		shape.x = Math.ceil(width / gridStep) * gridStep;
	} else {
		shape.x = vbranch;
	}

	shape.y = 0;
	shape.$width = kidsWidth;
	shape.$baseHeight = baseHeight ? baseHeight : shape.height + config.margin.itemY;
	shape.$height = kidsHeight + shape.$baseHeight;

	return kidsWidth + extra;
}

function setGlobalSizes(
	data: ShapesCollection,
	shape: IDataItem,
	left: number,
	top: number,
	config: IDiagramConfig,
	level: number
) {
	const kids = shape.$connection;
	const assistants = shape.$assistants;
	const partners = shape.$partners?.common;
	const vertical = shape.dir === "vertical";
	let localtop = 0;

	shape.x += left;
	shape.y += top;

	if (config.gridStep) {
		shape.y = Math.ceil(shape.y / config.gridStep) * config.gridStep;
	}

	if (assistants?.length) {
		const gapX = config.margin.itemX / 2;
		const midPointX = shape.x + shape.width / 2;

		let lastLefPointY = shape.y + shape.$baseHeight;
		let lastRightPointY = shape.y + shape.$baseHeight;

		for (let index = 0; index < assistants.length; index++) {
			const sub = data.getItem(assistants[index]);
			const height = sub.height + config.margin.itemY;

			if (!sub.hidden) {
				if (vertical) {
					sub.x = shape.x + gapX;
					sub.y = lastRightPointY;

					lastRightPointY += height;
				} else {
					if (sub.$even) {
						sub.y = lastRightPointY;
						sub.x = midPointX + gapX;

						lastRightPointY += height;
					} else {
						sub.y = lastLefPointY;
						sub.x = midPointX - (gapX + sub.width);

						lastLefPointY += height;
					}
				}
			}
		}
	}

	if (partners?.length) {
		const shapeY = shape.y + (shape.$baseHeight / 2 - (shape.height + config.margin.itemY) / 2);
		const offsetY = shapeY - shape.y;

		let lastLeftPointY = shapeY - offsetY + (shape.$baseHeight - shape.$partners.leftHeight) / 2;
		let lastRightPointY = shapeY - offsetY + (shape.$baseHeight - shape.$partners.rightHeight) / 2;

		if (partners.length > 1) {
			for (let index = 0; index < partners.length; index++) {
				const sub = data.getItem(partners[index]);
				const height = sub.height + config.margin.itemY;

				if (!sub.hidden) {
					if (sub.$even) {
						sub.x = shape.x + shape.width + config.margin.itemX;
						sub.y = lastRightPointY;

						lastRightPointY += height;
					} else {
						sub.x = shape.x - sub.width - config.margin.itemX;
						sub.y = lastLeftPointY;

						lastLeftPointY += height;
					}
				}
			}
		} else {
			shape.x = shape.x + shape.width / 2 + config.margin.itemX / 2;

			const sub = data.getItem(partners[0]);
			sub.x = shape.x - sub.width - config.margin.itemX;
			sub.y = lastLeftPointY;
		}

		shape.y = shapeY;
	}

	top += shape.$height;

	if (shape.open !== false && kids) {
		let sub;
		for (let i = 0; i < kids.length; i++) {
			sub = data.getItem(kids[i][1]);
			if (!sub.hidden) {
				if (!sub.assistant && !sub.partner) {
					const pos = setGlobalSizes(data, sub, left, top, config, level + 1);
					if (vertical) {
						top += pos;
						localtop += pos;
					} else {
						localtop = Math.max(localtop, pos);
						left += sub.$width + config.margin.itemX;
					}
				}
				directLinkPath(data.getItem(kids[i][0]) as ILineConfig, shape, sub, config);
			}
		}
	}

	return shape.$height + localtop;
}

export function placeOrgonogram(data: ShapesCollection, config: IDiagramConfig) {
	const roots = data.getRoots();

	if (roots.length !== 1) {
		return;
	}
	const root = data.getItem(roots[0]);

	setLocalSizes(data, root, config, 0, 0);
	setGlobalSizes(data, root, 0, 0, config, 0);
	setHeaderColor(data);
}
